import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  applyNodeChanges,
  Background,
  Controls,
  MiniMap,
  type Node,
  type NodeChange,
  ReactFlow,
  ReactFlowProvider,
} from '@xyflow/react'
import { X } from 'lucide-react'
import '@xyflow/react/dist/style.css'
import { RecipeGraphTransformer } from '@/services/transforms/recipeToGraph'
import { MetaRecette } from '@/types/recipe'
import { GraphNodeData } from '@/types/graph'
import { recipeNodeTypes } from '@/components/canvas/nodeTypes'
import { Button } from '@/components/ui/Button'
import { cn } from '@/utils/cn'

interface RecipePreviewCanvasProps {
  recipe: MetaRecette
  className?: string
  inspectable?: boolean
}

const nodeTypeColorMap: Record<string, string> = {
  // Category data colours, through the tokens (same mapping as the canvas
  // nodes: violet -> --primary, cyan -> --info, green -> --success,
  // orange -> --warning, indigo -> --foreground).
  question: 'hsl(var(--primary))',
  llm: 'hsl(var(--primary))',
  mcp: 'hsl(var(--info))',
  data: 'hsl(var(--success))',
  condition: 'hsl(var(--warning))',
  io: 'hsl(var(--foreground))',
}

const nodeTypeLegend = [
  { key: 'llm', label: 'LLM' },
  { key: 'mcp', label: 'MCP' },
  { key: 'data', label: 'Data' },
  { key: 'condition', label: 'Condition' },
  { key: 'io', label: 'IO' },
]

function PreviewFlow({
  recipe,
  className,
  inspectable = false,
}: RecipePreviewCanvasProps) {
  const { t } = useTranslation()
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null)
  const structuredSpec = recipe.structured_spec
  const hasStructuredSpec =
    structuredSpec &&
    Array.isArray(structuredSpec.nodes) &&
    Array.isArray(structuredSpec.edges)

  const graph = useMemo(
    () =>
      hasStructuredSpec
        ? RecipeGraphTransformer.specToGraph(structuredSpec)
        : { nodes: [], edges: [] },
    [hasStructuredSpec, structuredSpec],
  )
  // Parent callers (Agent Spec) often rebuild the recipe object each render;
  // key on serialized topology so local drag positions are not wiped.
  const layoutSourceKey = useMemo(() => {
    if (!hasStructuredSpec || !structuredSpec) {
      return `${recipe.id}:empty`
    }
    return `${recipe.id}:${JSON.stringify({
      nodes: structuredSpec.nodes,
      edges: structuredSpec.edges,
    })}`
  }, [hasStructuredSpec, recipe.id, structuredSpec])
  const [nodes, setNodes] = useState<Node<GraphNodeData>[]>([])
  const selectedNode = useMemo(
    () => nodes.find((node) => node.id === selectedNodeId) ?? null,
    [nodes, selectedNodeId],
  )

  // Reset layout from the workflow spec; do not depend on selection so
  // individual node drags survive click-to-inspect.
  useEffect(() => {
    setSelectedNodeId(null)
    setNodes(
      graph.nodes.map((node) => ({
        ...node,
        selected: false,
      })),
    )
    // graph.nodes is derived from the same structuredSpec as layoutSourceKey
  }, [layoutSourceKey])

  useEffect(() => {
    setNodes((current) =>
      current.map((node) => ({
        ...node,
        selected: inspectable && node.id === selectedNodeId,
      })),
    )
  }, [inspectable, selectedNodeId])

  const onNodesChange = useCallback(
    (changes: NodeChange<Node<GraphNodeData>>[]) => {
      if (!inspectable) return
      setNodes((current) => applyNodeChanges(changes, current))
    },
    [inspectable],
  )

  if (!hasStructuredSpec) {
    return (
      <div
        className={cn(
          'flex h-64 items-center justify-center rounded-xl border border-border bg-background px-6 text-center text-sm text-muted-foreground',
          className,
        )}
      >
        {t('historyDetail.noBlueprintPreview')}
      </div>
    )
  }

  const canvas = (
    <div
      className={cn(
        'recipe-preview-canvas relative h-64 min-h-0 overflow-hidden rounded-xl border border-border bg-background [&_.react-flow__node_button]:hidden',
        className,
      )}
    >
      <ReactFlow
        nodes={nodes}
        edges={graph.edges}
        onNodesChange={onNodesChange}
        nodeTypes={recipeNodeTypes}
        fitView
        fitViewOptions={{ padding: 0.12, maxZoom: 1 }}
        nodesDraggable={inspectable}
        nodesConnectable={false}
        elementsSelectable={inspectable}
        nodesFocusable={inspectable}
        onNodeClick={
          inspectable ? (_event, node) => setSelectedNodeId(node.id) : undefined
        }
        onPaneClick={inspectable ? () => setSelectedNodeId(null) : undefined}
        zoomOnDoubleClick={false}
        proOptions={{ hideAttribution: true }}
      >
        <Background color="hsl(var(--border))" gap={18} />
        {inspectable ? (
          <Controls position="top-left" showInteractive={false} />
        ) : null}
        <MiniMap
          pannable
          zoomable
          nodeColor={(node) => nodeTypeColorMap[node.type || 'llm'] || 'hsl(var(--muted-foreground))'}
          maskColor="hsl(var(--card-raised) / 0.6)"
        />
      </ReactFlow>
      {inspectable && !selectedNode ? (
        <div className="pointer-events-none absolute right-3 top-3 max-w-64 rounded-xl border border-border bg-background px-3 py-2 text-xs text-muted-foreground">
          {t('agentSpec.graphSelectNode')}
        </div>
      ) : null}
      <div className="pointer-events-none absolute bottom-3 left-3 rounded-xl border border-border bg-background px-3 py-2">
        <div className="flex flex-wrap gap-3 text-[11px] text-muted-foreground">
          {nodeTypeLegend.map((item) => (
            <div key={item.key} className="flex items-center gap-1.5">
              <span
                className="h-2.5 w-2.5 rounded-full"
                style={{ backgroundColor: nodeTypeColorMap[item.key] }}
                aria-hidden="true"
              />
              <span>{item.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )

  if (!inspectable || !selectedNode) return canvas

  return (
    <div className="grid min-h-0 gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(18rem,24rem)]">
      {canvas}
      <aside
        className={cn(
          'min-h-0 overflow-y-auto rounded-xl border border-border bg-background p-4',
          className,
        )}
        aria-label={t('agentSpec.graphNodeDetails')}
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs font-medium text-muted-foreground">
              {t('agentSpec.graphNodeDetails')}
            </p>
            <h3 className="truncate text-base font-semibold">
              {selectedNode.data.label || selectedNode.id}
            </h3>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-9 w-9 shrink-0"
            aria-label={t('agentSpec.graphCloseNodeDetails')}
            onClick={() => setSelectedNodeId(null)}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        <dl className="mb-4 grid gap-3 text-sm">
          <div>
            <dt className="text-xs text-muted-foreground">
              {t('agentSpec.graphNodeId')}
            </dt>
            <dd className="break-all font-mono text-xs">{selectedNode.id}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">
              {t('agentSpec.graphNodeType')}
            </dt>
            <dd className="mt-1 inline-flex rounded-full border border-border px-2 py-0.5 text-xs font-medium">
              {selectedNode.type || selectedNode.data.type || '—'}
            </dd>
          </div>
        </dl>
        <h4 className="mb-2 text-sm font-semibold">
          {t('agentSpec.graphNodeConfiguration')}
        </h4>
        <pre className="overflow-x-auto whitespace-pre-wrap break-words rounded-lg bg-muted/60 p-3 font-mono text-xs leading-5">
          {JSON.stringify(selectedNode.data.config ?? {}, null, 2)}
        </pre>
      </aside>
    </div>
  )
}

export default function RecipePreviewCanvas(props: RecipePreviewCanvasProps) {
  return (
    <ReactFlowProvider>
      <PreviewFlow {...props} />
    </ReactFlowProvider>
  )
}
